// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { createContext, useEffect, useRef, useMemo, useCallback, type ReactNode } from 'react';
import { AnalyticsManager } from '../core/AnalyticsManager';
import type { AnalyticsConfig, ConsentState } from '../types';

export interface AnalyticsContextValue {
  track: (event: string, props?: Record<string, string>) => void;
  page: (path: string, props?: Record<string, string>) => void;
  identify: (userId: string, traits?: Record<string, string>) => void;
  setUser: (userId: string, role: string) => void;
  consent: ConsentState;
}

export const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

interface AnalyticsProviderProps {
  config: AnalyticsConfig;
  children: ReactNode;
}

export function AnalyticsProvider({ config, children }: AnalyticsProviderProps) {
  const managerRef = useRef<AnalyticsManager | null>(null);

  useEffect(() => {
    const manager = new AnalyticsManager();
    manager.init(config);
    managerRef.current = manager;

    return () => {
      manager.destroy();
      managerRef.current = null;
    };
    // Re-init only if config identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.app, config.tenant, config.enabled, config.debug, config.plausible?.domain]);

  const track = useCallback((event: string, props?: Record<string, string>) => {
    managerRef.current?.track(event, props);
  }, []);

  const page = useCallback((path: string, props?: Record<string, string>) => {
    managerRef.current?.page(path, props);
  }, []);

  const identify = useCallback((userId: string, traits?: Record<string, string>) => {
    managerRef.current?.identify(userId, traits);
  }, []);

  const setUser = useCallback((userId: string, role: string) => {
    managerRef.current?.setUser(userId, role);
  }, []);

  const value = useMemo<AnalyticsContextValue>(
    () => ({
      track,
      page,
      identify,
      setUser,
      consent: { analytics: false },
    }),
    [track, page, identify, setUser]
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}
